import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    setSubscribed(true);
    setEmail('');
    setTimeout(() => setSubscribed(false), 3000);
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-gunmetal py-20 relative z-10">
      <div className="max-w-7xl mx-auto px-8 lg:px-16">
        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          {/* Left Column - Company Info */}
          <div>
            <h3 className="mb-4 leading-tight">
              <span className="block text-5xl text-white" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>IronCore Roofing</span>
              <span className="block text-5xl text-neon-green" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>&amp; Exteriors</span>
            </h3>
            <p className="text-sm text-gray-400 leading-relaxed max-w-md mb-8">
              Your trusted roofing and exterior specialists serving Pennsylvania. We deliver quality craftsmanship, reliable service, and lasting protection for your home. From roof repairs to complete exterior renovations, IronCore has you covered.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-4 mb-8">
              <a href="tel:717-280-2005" className="flex items-center gap-3 text-gray-400 hover:text-neon-green transition-colors cursor-pointer">
                <div className="w-10 h-10 flex items-center justify-center bg-matte-black rounded-lg">
                  <i className="ri-phone-fill text-neon-green text-lg"></i>
                </div>
                <span className="text-sm font-medium">717-280-2005</span>
              </a>
              <a href="mailto:Info@ironcoreroofingandexteriors.com" className="flex items-center gap-3 text-gray-400 hover:text-neon-green transition-colors cursor-pointer">
                <div className="w-10 h-10 flex items-center justify-center bg-matte-black rounded-lg">
                  <i className="ri-mail-fill text-neon-green text-lg"></i>
                </div>
                <span className="text-sm font-medium">Info@ironcoreroofingandexteriors.com</span>
              </a>
              <div className="flex items-start gap-3 text-gray-400">
                <div className="w-10 h-10 flex items-center justify-center bg-matte-black rounded-lg flex-shrink-0">
                  <i className="ri-map-pin-fill text-neon-green text-lg"></i>
                </div>
                <span className="text-sm font-medium pt-2">2173 Embassy Dr.<br />Lancaster, PA 17603</span>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex gap-4">
              <a href="https://www.facebook.com/ironcoreroofingandexteriors" target="_blank" rel="noopener noreferrer" className="w-12 h-12 flex items-center justify-center rounded-xl text-white text-xl bg-[#1877F2] hover:scale-110 hover:shadow-[0_0_20px_rgba(24,119,242,0.5)] transition-all duration-300 cursor-pointer">
                <i className="ri-facebook-fill"></i>
              </a>
              <a href="https://www.instagram.com/ironcore_roofing_and_exteriors" target="_blank" rel="noopener noreferrer" className="w-12 h-12 flex items-center justify-center rounded-xl text-white text-xl hover:scale-110 hover:shadow-[0_0_20px_rgba(225,48,108,0.5)] transition-all duration-300 cursor-pointer" style={{ background: 'linear-gradient(135deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)' }}>
                <i className="ri-instagram-fill"></i>
              </a>
              <a href="#" className="w-12 h-12 flex items-center justify-center rounded-xl text-white text-xl bg-[#EA4335] hover:scale-110 hover:shadow-[0_0_20px_rgba(234,67,53,0.5)] transition-all duration-300 cursor-pointer">
                <i className="ri-google-fill"></i>
              </a>
            </div>
          </div>

          {/* Right Column - Map & Quick Links */}
          <div className="space-y-8">
            {/* Office Location Map */}
            <div>
              <h4 className="text-base font-semibold mb-4 text-white flex items-center gap-2">
                <i className="ri-building-2-fill text-neon-green"></i>
                Office Location
              </h4>
              <div className="rounded-xl overflow-hidden border border-gray-700">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3049.8!2d-76.3!3d40.04!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c624d0d0d0d0d0%3A0x0!2s2173%20Embassy%20Dr%2C%20Lancaster%2C%20PA%2017603!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
                  width="100%"
                  height="200"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="IronCore Roofing Office Location"
                  className="transition-all duration-500"
                ></iframe>
              </div>
            </div>

            {/* Quick Links & Services */}
            <div className="grid grid-cols-2 gap-8">
              <div>
                <h4 className="text-base font-semibold mb-4 text-white">Quick Links</h4>
                <ul className="space-y-3">
                  <li>
                    <button
                      onClick={() => scrollToSection('about')}
                      className="text-sm text-gray-400 hover:text-neon-green transition-colors cursor-pointer"
                    >
                      About Us
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => scrollToSection('testimonials')}
                      className="text-sm text-gray-400 hover:text-neon-green transition-colors cursor-pointer"
                    >
                      Testimonials
                    </button>
                  </li>
                  <li>
                    <Link to="/contact" className="text-sm text-gray-400 hover:text-neon-green transition-colors cursor-pointer">
                      Free Estimate
                    </Link>
                  </li>
                  <li>
                    <a href="#" className="text-sm text-gray-400 hover:text-neon-green transition-colors">
                      Financing Options
                    </a>
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="text-base font-semibold mb-4 text-white">Our Services</h4>
                <ul className="space-y-3">
                  <li>
                    <a href="/services#roofing-section" className="text-sm text-gray-400 hover:text-neon-green transition-colors cursor-pointer">
                      Roof Replacement
                    </a>
                  </li>
                  <li>
                    <a href="/services#roofing-section" className="text-sm text-gray-400 hover:text-neon-green transition-colors cursor-pointer">
                      Roof Repairs
                    </a>
                  </li>
                  <li>
                    <a href="/services#storm-section" className="text-sm text-gray-400 hover:text-neon-green transition-colors cursor-pointer">
                      Storm Restoration
                    </a>
                  </li>
                  <li>
                    <a href="/services#siding-section" className="text-sm text-gray-400 hover:text-neon-green transition-colors cursor-pointer">
                      Siding &amp; Gutters
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            {/* Newsletter */}
            <div>
              <h4 className="text-base font-semibold mb-4 text-white">Stay Updated</h4>
              {subscribed ? (
                <div className="flex items-center gap-2 text-neon-green text-sm">
                  <i className="ri-check-line"></i>
                  Thanks for subscribing!
                </div>
              ) : (
                <form
                  id="newsletter-form"
                  data-readdy-form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    try {
                      const response = await fetch('https://readdy.ai/api/form/d6atrenmvg9ih2c7ar60', {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/x-www-form-urlencoded',
                        },
                        body: new URLSearchParams({ email }).toString(),
                      });
                      if (response.ok) {
                        setSubscribed(true);
                        setEmail('');
                        setTimeout(() => setSubscribed(false), 3000);
                      }
                    } catch {
                      // handle error silently
                    }
                  }}
                  className="flex gap-3"
                >
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="flex-1 bg-matte-black border border-gray-700 rounded-lg px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-neon-green transition-colors"
                    required
                  />
                  <button
                    type="submit"
                    className="px-6 py-3 bg-neon-green text-matte-black rounded-lg font-semibold text-sm hover:bg-neon-green/90 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    Subscribe
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-gray-700">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-400">
              © 2025 IronCore Roofing & Exteriors. All rights reserved. Serving Pennsylvania with pride.
            </p>
            <div className="flex items-center gap-6">
              <a href="#" className="text-sm text-gray-400 hover:text-neon-green transition-colors">
                Privacy Policy
              </a>
              <a href="#" className="text-sm text-gray-400 hover:text-neon-green transition-colors">
                Terms of Service
              </a>
              <a
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="text-sm text-gray-400 hover:text-neon-green transition-colors"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
